import { getFilterOperator } from '@core/services/filter';
import BaseModel from '@core/model';

import OfferStatusEnum from '@constants/enums/offer/status';

import ShortlistApi from '@packages/shortlists/api';

import { Investor, Adviser, Proposal } from '@/models';

export class Shortlist extends BaseModel {
    static name = 'Shortlist';
    static entity = 'shortlists';
    static Api = ShortlistApi;
    static runSetup = false;

    static fields() {
        return {
            ...super.fields(),
            id: this.attr(null),
            name: this.string(null).nullable(),
            last_edited_date: this.string(null).nullable(),
            entries: this.hasMany(Proposal, 'shortlist_id'),
            total_maximum_tax_relief: this.attr(null).nullable(),
            total_cleared_funds_required: this.attr(null).nullable(),

            investor_id: this.string(null).nullable(),
            investor: this.belongsTo(Investor, 'investor_id'),

            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id')
        };
    }

    get computed_name() {
        return ['Shortlist', this.name].filter(Boolean).join(' - ');
    }

    get has_investor_mismatch() {
        const investorId = this.investor_id || this.investor?.id || null;

        if (!investorId) {
            return false;
        }

        if (!this.investor) {
            return true;
        }

        if (this.investor.$responseFormat < 20) {
            return true;
        }

        if (this.investor.id && this.investor.id !== investorId) {
            return true;
        }

        return false;
    }

    async setupLogic() {
        const investorId = this.investor_id || this.investor?.id || null;

        if (this.has_investor_mismatch) {
            this.investor = await Investor.$get(investorId, {
                force: true
            });
            this.investor_id = investorId;
        }

        await Promise.all(this.entries.map(entry => entry.setup()));
    }

    get advisers() {
        if (!this.investor) {
            return [];
        }

        return this.investor.adviser_accounts.map(aa => aa.adviser);
    }

    get adviser_filter() {
        if (!this.investor || !this.advisers.length) {
            return null;
        }
        return {
            id: `${getFilterOperator('in')}:[${this.advisers.map(a => a.id).join(',')}]`
        };
    }

    get entry_offer_ids() {
        return this.entries.map(entry => entry.offer.id) || [];
    }

    get entry_quantity() {
        return this.entries.filter(entry => entry.offer.status !== OfferStatusEnum.CLOSED).length;
    }

    get total_investment_amount() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.amount?.amount) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.amount.amount).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_adviser_fees() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.adviser_fees?.amount) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.adviser_fees.amount).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_platform_fees() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.platform_fees?.amount) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.platform_fees.amount).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_initial_adviser_fees() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.$calc?.initial_adviser_fee_total) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.$calc.initial_adviser_fee_total).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_annual_adviser_fees() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.$calc?.annual_adviser_fee_total) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.$calc.annual_adviser_fee_total).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_annual_platform_fees() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.$calc?.annual_platform_fee_total) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.$calc.annual_platform_fee_total).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_required_cleared_funds() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.$calc?.total_amount_including_fees) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.$calc.total_amount_including_fees).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get total_amount_including_fees() {
        return this.entries.reduce(
            (total, entry) => {
                if (entry.$calc?.total_amount_including_fees) {
                    return {
                        currency: entry.currency,
                        amount: this.$m(total.amount).add(entry.$calc.total_amount_including_fees).value
                    };
                }

                return total;
            },
            { currency: 'GBP', amount: 0 }
        );
    }

    get offer_closed() {
        return this.entries.some(entry => entry.offer_closed);
    }

    get has_closed_allocation() {
        return this.entries.some(entry => entry.has_closed_allocation);
    }

    get has_cash() {
        return (this.investor?.cash?.amount || 0) > 0;
    }

    get has_cleared_funds() {
        const cash = this.investor?.cash?.amount || 0;
        const total = this.total_cleared_funds_required?.amount || 0;

        if (!total) {
            return true;
        }

        return cash >= total;
    }

    async $removeEntry(entryId) {
        if (!this.id) {
            throw new ReferenceError('Missing ID required while attempting to send DELETE request');
        }

        if (!entryId) {
            throw new ReferenceError('Missing Entry ID required while attempting to send DELETE request');
        }

        return await Shortlist.api.removeEntry(entryId, this.id);
    }
}

export default Shortlist;
